import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatRupiah } from "@/lib/format";
import { fileUrl } from "@/lib/api";
import { CheckCircle, Circle } from "@phosphor-icons/react";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-sm font-medium break-words">{value}</p>
    </div>
  );
}

export function ReimbursementDetailModal({ item, onClose }) {
  const r = item;
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Detail Reimbursement</DialogTitle></DialogHeader>
        {r && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="No" value={r.no || "—"} />
              <Field label="Tanggal" value={r.date || "—"} />
              <Field label="Jumlah" value={formatRupiah(r.amount)} />
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <Badge variant="outline" className={r.status === "paid" ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"}>
                  {r.status === "paid" ? "Paid" : "Unpaid"}
                </Badge>
              </div>
            </div>
            <Field label="Keterangan" value={r.description || "—"} />
            <div>
              <p className="text-xs text-muted-foreground mb-1">Foto Bon</p>
              {r.photo_path ? (
                <a href={fileUrl(r.photo_path)} target="_blank" rel="noreferrer" className="block mt-2 rounded-lg border overflow-hidden bg-muted hover:opacity-90 transition-opacity">
                  <img src={fileUrl(r.photo_path)} alt="Bon" className="w-full max-h-[70vh] object-contain mx-auto" loading="lazy" data-testid="reimbursement-detail-photo" />
                  <p className="text-[11px] text-center text-muted-foreground py-1 bg-background">Klik untuk perbesar</p>
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">Tidak ada foto bon yang diunggah.</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function KasbonDetailModal({ item, onClose, canManage = false, onToggle, lockedPeriods = [] }) {
  const c = item;
  const isLocked = (s) => lockedPeriods.some((p) => p.month === s.month && p.year === s.year);
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="kasbon-detail-modal">
        <DialogHeader><DialogTitle>Detail Kasbon {c?.no}</DialogTitle></DialogHeader>
        {c && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Keterangan" value={c.description || "—"} />
              <Field label="Total" value={formatRupiah(c.total_amount)} />
              <Field label="Jumlah Cicilan" value={`${c.installments}x`} />
              <Field label="Potongan / Bulan" value={formatRupiah(c.monthly_amount)} />
              {c.date && <Field label="Tanggal Pengajuan" value={c.date} />}
            </div>

            {c.schedule?.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">Jadwal Cicilan</p>
                  <span className="text-xs text-muted-foreground" data-testid="kasbon-progress">
                    {c.paid_count}/{c.installments} lunas · sisa {formatRupiah(c.remaining_amount)}
                  </span>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {c.schedule.map((s, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`installment-row-${i}`}>
                      <div className="flex items-center gap-2.5">
                        {s.paid ? (
                          <CheckCircle size={20} weight="fill" className="text-[hsl(var(--success))]" />
                        ) : (
                          <Circle size={20} className="text-muted-foreground" />
                        )}
                        <div>
                          <p className="text-sm font-medium">Cicilan {s.no} · {s.label}</p>
                          <p className={`text-xs ${s.paid ? "text-[hsl(var(--success))]" : "text-muted-foreground"}`}>
                            {s.paid ? "Sudah dipotong dari gaji" : "Belum dipotong"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm tabular-nums font-medium">{formatRupiah(s.amount)}</span>
                        {canManage && (
                          <Button size="sm" variant={s.paid ? "outline" : "success"} disabled={isLocked(s)} title={isLocked(s) ? "Periode ini sudah ditutup. Buka buku dengan PIN untuk mengubah." : undefined} onClick={() => onToggle(i, !s.paid)} data-testid={`toggle-installment-${i}`}>
                            {s.paid ? "Batalkan" : "Tandai Dipotong"}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {c.paid_count === c.installments && (
                  <p className="mt-3 text-sm font-semibold text-[hsl(var(--success))] flex items-center gap-1.5">
                    <CheckCircle size={16} weight="fill" /> Cicilan telah LUNAS.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Jadwal cicilan akan tersedia setelah kasbon disetujui admin.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
